/**
 * Call audio tones using Web Audio API.
 *
 * - startRinging()  → incoming call ringtone (pleasant arpeggio, for callee)
 * - startRingback() → outgoing ringback tone (440+480 Hz, for caller)
 * - stopRinging()   → stops whichever tone is playing
 */

let audioCtx: AudioContext | null = null;
let activeHandle: ReturnType<typeof setTimeout> | null = null;
let activeGain: GainNode | null = null;
// Track which type is active so we don't accidentally double-start

function getAudioContext(): AudioContext {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
  }
  return audioCtx;
}

// ─── Outgoing ringback (440 + 480 Hz, classic telephony) ───────────────────

function playRingbackBurst(ctx: AudioContext, gain: GainNode, startTime: number, duration: number): void {
  [440, 480].forEach((freq) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const envGain = ctx.createGain();
    envGain.gain.setValueAtTime(0, startTime);
    envGain.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
    envGain.gain.setValueAtTime(0.3, startTime + duration - 0.01);
    envGain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.connect(envGain);
    envGain.connect(gain);
    osc.start(startTime);
    osc.stop(startTime + duration);
  });
}

function scheduleRingbackCycle(ctx: AudioContext, gain: GainNode, baseTime: number): number {
  // Standard ringback: 2s tone, 4s silence → 6s cycle
  playRingbackBurst(ctx, gain, baseTime, 2.0);
  return 6000;
}

// ─── Incoming ringtone (pleasant two-note arpeggio) ────────────────────────

function playNote(
  ctx: AudioContext,
  gain: GainNode,
  freq: number,
  startTime: number,
  duration: number,
  volume: number = 0.25,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = freq;

  const envGain = ctx.createGain();
  envGain.gain.setValueAtTime(0, startTime);
  envGain.gain.linearRampToValueAtTime(volume, startTime + 0.01);
  envGain.gain.setValueAtTime(volume, startTime + duration - 0.02);
  envGain.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.connect(envGain);
  envGain.connect(gain);
  osc.start(startTime);
  osc.stop(startTime + duration);
}

function scheduleRingCycle(ctx: AudioContext, gain: GainNode, baseTime: number): number {
  // Musical arpeggio pattern: C5-E5-G5-C6 (twice), then 2s silence
  // Total: ~3.2s per cycle
  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
  const noteLen = 0.15;
  const noteGap = 0.05;
  const groupGap = 0.25;
  const pause = 2.0;

  let t = baseTime;
  // First group
  for (const freq of notes) {
    playNote(ctx, gain, freq, t, noteLen, 0.25);
    t += noteLen + noteGap;
  }
  t += groupGap;
  // Second group (slightly softer)
  for (const freq of notes) {
    playNote(ctx, gain, freq, t, noteLen, 0.18);
    t += noteLen + noteGap;
  }

  const cycleDuration = t - baseTime + pause;
  return cycleDuration * 1000;
}

// ─── Public API ────────────────────────────────────────────────────────────

function startTone(type: 'ring' | 'ringback'): void {
  if (activeHandle !== null) return; // already playing something

  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();

    activeGain = ctx.createGain();
    activeGain.gain.value = 1;
    activeGain.connect(ctx.destination);

    const scheduleFn = type === 'ring' ? scheduleRingCycle : scheduleRingbackCycle;

    function loop() {
      if (activeHandle === null && activeGain === null) return;
      const c = getAudioContext();
      const g = activeGain!;
      const ms = scheduleFn(c, g, c.currentTime);
      activeHandle = setTimeout(loop, ms);
    }

    loop();
  } catch {
    // Web Audio API not available — silent fail
  }
}

/** Start incoming call ringtone (for callee) */
export function startRinging(): void {
  startTone('ring');
}

/** Start outgoing ringback tone (for caller) */
export function startRingback(): void {
  startTone('ringback');
}

/** Stop any active ring/ringback tone */
export function stopRinging(): void {
  if (activeHandle !== null) {
    clearTimeout(activeHandle);
    activeHandle = null;
  }
  if (activeGain) {
    try {
      activeGain.gain.cancelScheduledValues(activeGain.context.currentTime);
      activeGain.gain.setValueAtTime(0, activeGain.context.currentTime);
      activeGain.disconnect();
    } catch {
      // ignore
    }
    activeGain = null;
  }
}
